import { useEffect, useMemo, useRef } from "react";

export interface DebouncedFunction<T extends unknown[]> {
  (...args: T): void;
  /** Clears any pending call without invoking the callback. */
  cancel: () => void;
}

/**
 * Returns a debounced version of `callback` that fires after `delay` ms of
 * inactivity. The timer is cleared on unmount automatically. Call `.cancel()`
 * on the returned function to drop a pending call without firing it.
 */
export function useDebounce<T extends unknown[]>(callback: (...args: T) => void, delay: number): DebouncedFunction<T> {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return useMemo(() => {
    const debounced = ((...args: T) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    }) as DebouncedFunction<T>;

    debounced.cancel = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };

    return debounced;
  }, [delay]);
}
